import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import dayjs from "dayjs";
import { RxDto, RxSeriesDto, WaitListEntryDto } from "../../api-client";
import {
  useAddOrRemoveFromWaitlist,
  usePatientRxSeries,
  usePatientWaitlistStats,
} from "../../stores/Patients";
import Controls from "../../atoms/Controls";
import { useAllUsersBasic } from "../../stores/User/user.queries";

interface WaitListDataEntryDialogProps {
  open: boolean;
  patientId: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const createDefaultEntry = (): WaitListEntryDto => ({
  id: undefined,
  note: "",
  start: new Date().toISOString(),
  end: undefined,
  rxSeriesId: undefined,
  rxId: undefined,
  therapistId: undefined,
  isWaiting: true,
});

// Ensures ALL fields are always sent to API
const normalizeWaitListEntry = (entry: WaitListEntryDto): WaitListEntryDto => ({
  id: entry.id ?? undefined,
  note: entry.note ?? "",
  start: entry.start ?? new Date().toISOString(),
  end: entry.end ?? undefined,
  rxSeriesId: entry.rxSeriesId ?? undefined,
  rxId: entry.rxId ?? undefined,
  therapistId: entry.therapistId ?? undefined,
  isWaiting: entry.isWaiting ?? true,
});

const WaitListDataEntryDialog: React.FC<WaitListDataEntryDialogProps> = ({
  open,
  patientId,
  onClose,
  onSuccess,
}) => {
  const [waitListDto, setWaitListDto] = useState<WaitListEntryDto[]>([createDefaultEntry()]);

  const [rxSeriesOptions, setRxSeriesOptions] = useState<RxSeriesDto[]>([]);
  const [rxOptions, setRxOptions] = useState<RxDto[]>([]);
  const { data: allUsersBasicData = [] } = useAllUsersBasic(false);

  const [filterSeries, setFilterSeries] = useState<string | undefined>(undefined);
  const [filterRx, setFilterRx] = useState<string | undefined>(undefined);

  const { data: patientRxSeriesData, isLoading: isRxSeriesLoading } = usePatientRxSeries(patientId);
  const { data: waitListData, isLoading: isWaitlistLoading } = usePatientWaitlistStats(patientId);

  const waitlistMutation = useAddOrRemoveFromWaitlist();

  useEffect(() => {
    if (waitListData && waitListData.length > 0) {
      setWaitListDto(waitListData.map(normalizeWaitListEntry));
    } else {
      setWaitListDto([createDefaultEntry()]);
    }
  }, [waitListData, open]);

  useEffect(() => {
    if (patientRxSeriesData) {
      setRxSeriesOptions(patientRxSeriesData.rxSeries || []);
      setRxOptions(patientRxSeriesData.rxIds || []);
    }
  }, [patientRxSeriesData]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value, id } = event.target;

    setWaitListDto((prev) => {
      const updated = [...prev];
      (updated[index] as any)[name] =
        (name === "rxSeriesId" || name === "rxId" || name === "therapistId") && id ? id : value;
      return updated;
    });
  };

  const handleAddEntry = () => {
    setWaitListDto((prev) => [...prev, createDefaultEntry()]);
  };

  const handleDeleteEntry = (index: number) => {
    const entry = waitListDto[index];

    if (entry?.id) {
      waitlistMutation.mutate(
        {
          id: patientId,
          waitListDto: [normalizeWaitListEntry(entry)],
          action: "remove",
        },
        {
          onSuccess: () => {
            setWaitListDto((prev) => prev.filter((_, i) => i !== index));
            onSuccess?.();
          },
        },
      );
    } else {
      setWaitListDto((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    const payload = waitListDto.map(normalizeWaitListEntry);

    waitlistMutation.mutate(
      {
        id: patientId,
        waitListDto: payload,
        action: "add",
      },
      {
        onSuccess: () => {
          onClose();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        Patient zur Warteliste hinzufügen
      </DialogTitle>
      {isRxSeriesLoading || isWaitlistLoading ? (
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      ) : (
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: "900px", overflowY: "auto" }}>
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <Button size="small" onClick={handleAddEntry} color="primary" variant="contained">
              <Tooltip title="Wartelisteneintrag hinzufügen">
                <Add fontSize="small" />
              </Tooltip>
            </Button>
          </Box>
          {waitListDto.map((entry, index) => (
            <Paper key={index} sx={{ p: 2, position: "relative" }}>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteEntry(index)}
                sx={{ position: "absolute", top: 2, right: 8 }}
              >
                <Delete fontSize="small" />
              </IconButton>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Notiz"
                  name="note"
                  value={entry.note}
                  onChange={(e: any) => handleChange(e, index)}
                  multiline
                  rows={2}
                  fullWidth
                />
                <Controls.DatePicker
                  label="Startdatum"
                  name="start"
                  value={dayjs(entry.start).format("YYYY-MM-DD")}
                  onChange={(e: any) => handleChange(e, index)}
                />
                <Controls.DatePicker
                  label="Enddatum"
                  name="end"
                  value={entry.end ? dayjs(entry.end).format("YYYY-MM-DD") : ""}
                  onChange={(e: any) => handleChange(e, index)}
                />
                <Controls.Select
                  label="Serie"
                  name="rxSeriesId"
                  value={
                    rxSeriesOptions?.find((option) => option.id === entry.rxSeriesId)?.label || ""
                  }
                  onChange={(e: any) => {
                    const value = e.target.value || undefined;
                    handleChange(e, index);
                  }}
                  options={rxSeriesOptions.map((x) => ({
                    value: x.label,
                    label: x.label,
                    id: x.id,
                  }))}
                />{" "}
                <Controls.Select
                  label="Rezeptnummer"
                  name="rxId"
                  value={rxOptions?.find((option) => option.id === entry.rxId)?.rxNumber || ""}
                  onChange={(e: any) => {
                    const value = e.target.value || undefined;
                    handleChange(e, index);
                  }}
                  options={rxOptions.map((x) => ({
                    value: x.rxNumber,
                    label: x.rxNumber,
                    id: x.id,
                  }))}
                />
                <Controls.Select
                  label="Therapeut"
                  name="therapistId"
                  value={
                    allUsersBasicData?.find((option) => option.id === entry.therapistId)
                      ? `${allUsersBasicData.find((option) => option.id === entry.therapistId)?.firstName} ${allUsersBasicData.find((option) => option.id === entry.therapistId)?.lastName}`
                      : ""
                  }
                  onChange={(e: any) => {
                    const value = e.target.value || undefined;
                    handleChange(e, index);
                  }}
                  options={allUsersBasicData.map((x) => ({
                    value: `${x.firstName} ${x.lastName}`,
                    label: `${x.firstName} ${x.lastName}`,
                    id: x.id,
                  }))}
                />
              </Box>
            </Paper>
          ))}
        </DialogContent>
      )}

      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleSave}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaitListDataEntryDialog;
